import { useCallback, type KeyboardEvent } from 'react';
import type { FindingAttribute } from '@/models/template';
import FindingHierarchyCompletion from '../../FindingHierarchyCompletion';
import styles from './AddStatementRow.module.css';

interface AddStatementRowProps {
  inputValue: string;
  onInputChange: (val: string) => void;
  onAdd: (statement: string) => void;
  attributes: FindingAttribute[];
  regionLabel?: string;
  disabled?: boolean;
  findingClassLabels?: { findingClass: string; label: string }[];
  /** Statements already committed to the parent input — used by the
   *  dropdown picker to highlight pills that are already selected. */
  selectedStatements?: string[];
  /** Batch replace of the committed statement list (single-select, "All normal"). */
  onSetStatements?: (next: string[]) => void;
}

export default function AddStatementRow({
  inputValue,
  onInputChange,
  onAdd,
  attributes,
  regionLabel,
  disabled = false,
  findingClassLabels,
  selectedStatements,
  onSetStatements,
}: AddStatementRowProps) {
  // Handle Enter key on the wrapper — fires after the completion's handler bubbles up.
  // If the completion handled Enter (selected an option), e.defaultPrevented is true
  // and we skip. Otherwise the user pressed Enter with plain text to add a statement.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      e.preventDefault();
      onAdd(trimmed);
    },
    [inputValue, onAdd]
  );

  // Typed text becomes a statement when focus leaves the row, like a statement
  // row commits on blur. Picking a pill never blurs (the panel prevents
  // mousedown), so this cannot double-add.
  const handleBlur = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed) onAdd(trimmed);
  }, [inputValue, onAdd]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard-only event capture wrapper; no interactive role needed
    <div className={styles.row} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <FindingHierarchyCompletion
        value={inputValue}
        onChange={onInputChange}
        attributes={attributes}
        regionLabel={regionLabel}
        disabled={disabled}
        findingClassLabels={findingClassLabels}
        selectedStatements={selectedStatements}
        onSetStatements={onSetStatements}
      />
    </div>
  );
}
